import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import enTranslations from '../translations/en.json';
import frTranslations from '../translations/fr.json';
import deTranslations from '../translations/de.json';
import itTranslations from '../translations/it.json';
import esTranslations from '../translations/es.json';
import ptTranslations from '../translations/pt.json';

const LanguageContext = createContext();

// Iter 95 — parité complète 6 locales : un seul dictionnaire consolidé par
// langue (translations/{loc}.json). Plus aucun merge landing nécessaire.
const SUPPORTED = {
  en: { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧', dict: enTranslations },
  fr: { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', dict: frTranslations },
  de: { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', dict: deTranslations },
  it: { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹', dict: itTranslations },
  es: { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', dict: esTranslations },
  pt: { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹', dict: ptTranslations },
};

const STORAGE_KEY = 'app_language';
const LINK_LANG_KEY = 'link_language'; // iter-122d — langue transmise par un lien partagé (code ISO, session, non personnel)

const detectInitial = () => {
  if (typeof window === 'undefined') return 'en';
  // 1. Explicit user choice persisted (toujours prioritaire sur la langue d'un lien)
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED[stored]) return stored;
  } catch {
    /* ignore */
  }
  // 2. iter-122d — ?lang=xx d'un lien partagé (Scorecard FR → landing FR → diagnostic FR → inscription FR),
  //    mémorisé pour la session afin de survivre aux navigations/refresh ; un choix manuel ultérieur l'écrase (1.)
  try {
    const fromUrl = (new URLSearchParams(window.location.search).get('lang') || '').toLowerCase();
    if (SUPPORTED[fromUrl]) {
      window.sessionStorage.setItem(LINK_LANG_KEY, fromUrl);
      return fromUrl;
    }
    const linked = window.sessionStorage.getItem(LINK_LANG_KEY);
    if (linked && SUPPORTED[linked]) return linked;
  } catch {
    /* ignore */
  }
  // 3. Browser locale (fr / fr-CH → fr, de → de, it → it, es → es, pt → pt)
  const nav = (window.navigator?.languages?.[0] || window.navigator?.language || 'en').toLowerCase();
  const base = nav.split('-')[0];
  if (SUPPORTED[base]) return base;
  return 'en';
};

const getNested = (obj, path) => path.split('.').reduce((cur, k) => (cur ? cur[k] : undefined), obj);

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(detectInitial);

  const changeLanguage = (lang) => {
    if (!SUPPORTED[lang]) return;
    setCurrentLanguage(lang);
    // keep the server-side profile in sync so navigation/emails match
    try {
      const token = window.localStorage.getItem('token');
      if (token) {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/language`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ language: lang }),
        }).catch(() => {});
      }
    } catch {
      /* ignore */
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
      // Any explicit language decision (header switcher, banner, profile) counts
      // as a decision — suppress the suggestion banner from ever nagging again.
      const existing = window.localStorage.getItem('lang_suggestion_state');
      if (!existing) {
        window.localStorage.setItem(
          'lang_suggestion_state',
          JSON.stringify({ decided: true, action: 'manual', at: Date.now() })
        );
      }
    } catch {
      /* ignore */
    }
  };

  // Iter 123 — langue transmise par un lien / une page géo (session) : jamais prioritaire sur un choix manuel.
  const applyLinkLanguage = (lang) => {
    const code = (lang || '').toLowerCase();
    if (!SUPPORTED[code]) return;
    try {
      if (window.localStorage.getItem(STORAGE_KEY)) return;
    } catch {
      /* ignore */
    }
    try {
      window.sessionStorage.setItem(LINK_LANG_KEY, code);
    } catch {
      /* ignore */
    }
    setCurrentLanguage(code);
  };

  // Set <html lang="…"> so screen readers + browser features behave correctly
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const tag = currentLanguage === 'fr' ? 'fr-CH'
        : currentLanguage === 'de' ? 'de-CH'
        : currentLanguage === 'it' ? 'it-CH'
        : currentLanguage === 'es' ? 'es-ES'
        : currentLanguage === 'pt' ? 'pt-PT'
        : 'en';
      document.documentElement.setAttribute('lang', tag);
    }
  }, [currentLanguage]);

  /** Translate a key. Falls back to English, then to the provided default, then to the key itself. */
  const t = (key, defaultValue = null) => {
    const primary = getNested(SUPPORTED[currentLanguage]?.dict, key);
    if (primary !== undefined && primary !== null && primary !== '') return primary;
    const english = getNested(SUPPORTED.en.dict, key);
    if (english !== undefined && english !== null && english !== '') return english;
    if (defaultValue) return defaultValue;
    return key;
  };

  const value = useMemo(
    () => ({
      currentLanguage,
      changeLanguage,
      applyLinkLanguage,
      setLanguageFromProfile: (lang) => changeLanguage(lang),
      t,
      translations: SUPPORTED[currentLanguage]?.dict || enTranslations,
      getTranslations: (key) => getNested(SUPPORTED[currentLanguage]?.dict || enTranslations, key) || {},
      getAvailableLanguages: () => Object.values(SUPPORTED).map(({ dict: _d, ...rest }) => rest),
      getCurrentLanguageInfo: () => {
        const { dict: _d, ...rest } = SUPPORTED[currentLanguage] || SUPPORTED.en;
        return rest;
      },
      isLanguageSupported: (lang) => !!SUPPORTED[lang],
      isRTL: false,
      isLoading: false,
      availableLanguages: Object.keys(SUPPORTED),
      languageSource: 'static',
    }),
    [currentLanguage]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within a LanguageProvider');
  return context;
};

export default LanguageContext;
